import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PHONE_DISPLAY, PHONE_TEL } from "@/data/marvinContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EstimateFooter = ({ t, lang }) => {
  const [form, setForm] = useState({ name: "", phone: "", address_zip: "", problem: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showStickyCall, setShowStickyCall] = useState(true);

  useEffect(() => {
      const estimateSection = document.getElementById("estimate");
      const pricingSection = document.getElementById("pricing");
      if (!estimateSection) return undefined;

    const updateStickyVisibility = () => {
      const rect = estimateSection.getBoundingClientRect();
      const pricingRect = pricingSection?.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const isEstimateVisible = rect.top < viewportHeight * 0.78 && rect.bottom > viewportHeight * 0.18;
      const isPricingVisible = pricingRect ? pricingRect.top < viewportHeight * 0.86 && pricingRect.bottom > viewportHeight * 0.2 : false;
      setShowStickyCall(!isEstimateVisible && !isPricingVisible);
    };

    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCall(!entry.isIntersecting),
      { root: null, threshold: 0.04, rootMargin: "-72px 0px -20% 0px" }
    );

    updateStickyVisibility();
    observer.observe(estimateSection);
    window.addEventListener("scroll", updateStickyVisibility, { passive: true });
    window.addEventListener("resize", updateStickyVisibility);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updateStickyVisibility);
      window.removeEventListener("resize", updateStickyVisibility);
    };
  }, []);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submitEstimate = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.address_zip.trim() || !form.problem.trim()) {
      toast.error(t.form.error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates`, { ...form, language: lang });
      toast.success(t.form.success);
      setForm({ name: "", phone: "", address_zip: "", problem: "" });
    } catch (error) {
      toast.error(t.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section id="estimate" data-testid="estimate-section" className="bg-[#0A2540] py-16 text-white sm:py-20">
        <div className="section-shell grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
          <div className="text-left">
            <p data-testid="estimate-label" className="text-sm font-extrabold uppercase tracking-[0.22em] text-amber-300">{t.form.label}</p>
            <h2 data-testid="estimate-title" className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">{t.form.title}</h2>
            <p data-testid="estimate-description" className="mt-4 text-base leading-relaxed text-blue-100 sm:text-lg">{t.form.text}</p>
            <div data-testid="estimate-call-card" className="mt-8 rounded-[1.5rem] border border-white/10 bg-white/10 p-5 backdrop-blur">
              <p data-testid="estimate-call-help" className="text-sm font-bold text-blue-100">{t.form.callHelp}</p>
              <a data-testid="estimate-phone-link" href={`tel:${PHONE_TEL}`} className="mt-2 inline-flex items-center gap-3 font-heading text-3xl font-extrabold text-amber-300 transition-transform hover:-translate-y-0.5">
                <Phone className="h-7 w-7" aria-hidden="true" />{PHONE_DISPLAY}
              </a>
            </div>
          </div>

          <form data-testid="estimate-form" onSubmit={submitEstimate} className="rounded-[2rem] bg-white p-5 text-left text-slate-900 shadow-2xl shadow-blue-950/30 sm:p-7">
            <div className="grid gap-4 sm:grid-cols-2">
              <label data-testid="estimate-name-field" className="grid gap-2 text-sm font-extrabold text-slate-700">
                {t.form.name}
                <Input data-testid="estimate-name-input" value={form.name} onChange={(event) => updateField("name", event.target.value)} placeholder={t.form.namePlaceholder} className="min-h-12 rounded-2xl border-slate-200 bg-slate-50" />
              </label>
              <label data-testid="estimate-phone-field" className="grid gap-2 text-sm font-extrabold text-slate-700">
                {t.form.phone}
                <Input data-testid="estimate-phone-input" value={form.phone} onChange={(event) => updateField("phone", event.target.value)} placeholder={t.form.phonePlaceholder} className="min-h-12 rounded-2xl border-slate-200 bg-slate-50" />
              </label>
            </div>
            <label data-testid="estimate-address-field" className="mt-4 grid gap-2 text-sm font-extrabold text-slate-700">
              {t.form.address}
              <Input data-testid="estimate-address-input" value={form.address_zip} onChange={(event) => updateField("address_zip", event.target.value)} placeholder={t.form.addressPlaceholder} className="min-h-12 rounded-2xl border-slate-200 bg-slate-50" />
            </label>
            <label data-testid="estimate-problem-field" className="mt-4 grid gap-2 text-sm font-extrabold text-slate-700">
              {t.form.problem}
              <Textarea data-testid="estimate-problem-input" value={form.problem} onChange={(event) => updateField("problem", event.target.value)} placeholder={t.form.problemPlaceholder} className="min-h-36 rounded-2xl border-slate-200 bg-slate-50" />
            </label>
            <Button data-testid="estimate-submit-button" type="submit" disabled={submitting} className="mt-5 min-h-14 w-full rounded-full bg-amber-500 text-base font-extrabold text-[#0A2540] shadow-lg shadow-amber-500/25 transition-transform hover:-translate-y-1 hover:bg-amber-600">
              <Send className="h-5 w-5" aria-hidden="true" />{submitting ? t.form.submitting : t.form.submit}
            </Button>
          </form>
        </div>
      </section>

      <footer data-testid="site-footer" className="bg-[#071A2D] pb-24 pt-12 text-white sm:pb-12">
        <div className="section-shell grid gap-8 text-left md:grid-cols-3">
          <div>
            <p data-testid="footer-brand" className="font-heading text-2xl font-extrabold">Marvin Plumbing</p>
            <a data-testid="footer-phone-link" href={`tel:${PHONE_TEL}`} className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-full bg-amber-400 px-4 font-extrabold text-[#0A2540] transition-transform hover:-translate-y-0.5"><Phone className="h-4 w-4" aria-hidden="true" />{PHONE_DISPLAY}</a>
          </div>
          <div>
            <p data-testid="footer-area-title" className="font-extrabold text-amber-300">{t.footer.areaTitle}</p>
            <p data-testid="footer-area-text" className="mt-2 text-sm leading-6 text-blue-100">{t.footer.area}</p>
          </div>
          <div>
            <p data-testid="footer-hours" className="text-sm leading-6 text-blue-100">{t.footer.hours}</p>
          </div>
        </div>
      </footer>

      {showStickyCall && (
        <a data-testid="mobile-sticky-call-button" href={`tel:${PHONE_TEL}`} className="fixed bottom-20 left-4 z-40 inline-flex min-h-12 max-w-[calc(100%-8rem)] items-center justify-center gap-2 rounded-full bg-amber-500 px-5 font-heading text-sm font-extrabold text-[#0A2540] shadow-2xl shadow-amber-900/30 transition-transform active:scale-[0.98] sm:hidden">
          <Phone className="h-4 w-4" aria-hidden="true" />{PHONE_DISPLAY}
        </a>
      )}
    </>
  );
};